import { RefusePart, RefusePrecision } from '@cf-shared/data-model/food';
import { BOOT_LOCALE, LL } from 'i18n/i18n.service';
import { TranslatedString } from 'models/translated-string';
import { formatList } from 'util/formatting';
import type { TranslationFunctions } from '../../../gensrc/i18n/i18n-types';

const KNOWN_PARTS: readonly string[] = Object.values(RefusePart);

/**
 * The uneaten share from a stored `refuse` entry, or undefined when it has none usable: only a number
 * above 0 and below 100 counts. Left unrounded, because the "Weighed with peel" checkbox (#5544)
 * computes with it; the hint rounds it for display.
 */
export function usableRefusePercent(refuse: unknown): number | undefined {
  if (refuse == null || typeof refuse !== 'object') {
    return undefined;
  }
  const rawPercent = (refuse as { percent?: unknown }).percent;
  return typeof rawPercent === 'number' && rawPercent > 0 && rawPercent < 100 ? rawPercent : undefined;
}

/** a stored percent as it is shown: a whole number, at least 1 */
export function displayRefusePercent(percent: number): number {
  return Math.max(1, Math.round(percent));
}

/**
 * The known parts of a stored `refuse` entry joined into one translated phrase ("husk and cob"), or
 * undefined when none can be named. Unknown and repeated parts are skipped, and a value that is not
 * a list counts as no parts.
 */
export function refusePartsText(refuse: unknown, ll: TranslationFunctions = LL, locale: string = BOOT_LOCALE): TranslatedString | undefined {
  const rawParts = refuse != null && typeof refuse === 'object' ? (refuse as { parts?: unknown }).parts : undefined;
  const parts: RefusePart[] = [];
  for (const part of Array.isArray(rawParts) ? rawParts : []) {
    // if the part is not one the app can name, or was already listed, skip it
    if (typeof part === 'string' && KNOWN_PARTS.includes(part) && !parts.includes(part as RefusePart)) {
      parts.push(part as RefusePart);
    }
  }
  return parts.length > 0 ? formatList(parts.map(part => partName(part, ll)), locale) : undefined;
}

/** whether a stored `refuse` entry says its share is only a rough guide; a missing or unknown value reads as typical */
export function isRoughRefuse(refuse: unknown): boolean {
  return refuse != null && typeof refuse === 'object' && (refuse as { precision?: unknown }).precision === RefusePrecision.Rough;
}

/** the translated name of each uneaten part, as it reads after "without" */
function partName(part: RefusePart, ll: TranslationFunctions): TranslatedString {
  const names: Record<RefusePart, () => TranslatedString> = {
    [RefusePart.Shell]: ll.editIngredient.refuseParts.shell,
    [RefusePart.Tail]: ll.editIngredient.refuseParts.tail,
    [RefusePart.Bone]: ll.editIngredient.refuseParts.bone,
    [RefusePart.Skin]: ll.editIngredient.refuseParts.skin,
    [RefusePart.Fat]: ll.editIngredient.refuseParts.fat,
    [RefusePart.Peel]: ll.editIngredient.refuseParts.peel,
    [RefusePart.Pit]: ll.editIngredient.refuseParts.pit,
    [RefusePart.Seeds]: ll.editIngredient.refuseParts.seeds,
    [RefusePart.Core]: ll.editIngredient.refuseParts.core,
    [RefusePart.Stem]: ll.editIngredient.refuseParts.stem,
    [RefusePart.Leaves]: ll.editIngredient.refuseParts.leaves,
    [RefusePart.Rind]: ll.editIngredient.refuseParts.rind,
    [RefusePart.Pod]: ll.editIngredient.refuseParts.pod,
    [RefusePart.Husk]: ll.editIngredient.refuseParts.husk,
    [RefusePart.Cob]: ll.editIngredient.refuseParts.cob,
    [RefusePart.Top]: ll.editIngredient.refuseParts.top,
    [RefusePart.Root]: ll.editIngredient.refuseParts.root,
    [RefusePart.Trimmings]: ll.editIngredient.refuseParts.trimmings,
  };
  return names[part]();
}

/**
 * The "weigh without" hint for the amount screen (food staples plan 4a, #5594), or undefined when the
 * food has nothing usable to say. The refuse entry comes from a stored food document, which may be
 * damaged or written before plan 4a added `parts`, so each field is checked as it is read:
 * - the percent counts only when it is a number above 0 and below 100, shown as a whole number, at least 1;
 * - only known parts count, each once; a value that is not a list counts as no parts;
 * - a rough precision keeps the percent but words it as a rough guide (owner, 2026-09-17).
 * A food copied into the user's own database before plan 4a has a percent but no parts, and gets the
 * shorter text. See design-docs/food-staples.md, "Plan 4a".
 */
export function buildRefuseHint(refuse: unknown, ll: TranslationFunctions = LL, locale: string = BOOT_LOCALE): TranslatedString | undefined {
  if (refuse == null || typeof refuse !== 'object') {
    return undefined;
  }
  // a rough percent is still given, but the text steers towards weighing after preparing; an unknown
  // or missing precision (a copy made before the field existed) reads as typical
  const isRough = isRoughRefuse(refuse);
  const usablePercent = usableRefusePercent(refuse);
  const percent = usablePercent === undefined ? undefined : displayRefusePercent(usablePercent);
  const partsText = refusePartsText(refuse, ll, locale);
  if (partsText !== undefined && percent !== undefined) {
    return isRough
      ? ll.editIngredient.refuseHint.withPartsRough({ parts: partsText, percent })
      : ll.editIngredient.refuseHint.withParts({ parts: partsText, percent });
  }
  if (percent !== undefined) {
    return isRough
      ? ll.editIngredient.refuseHint.percentOnlyRough({ percent })
      : ll.editIngredient.refuseHint.percentOnly({ percent });
  }
  if (partsText !== undefined) {
    return ll.editIngredient.refuseHint.partsOnly({ parts: partsText });
  }
  return undefined;
}
